import React, { useMemo, useState } from 'react';
import { CompositionData, CompositionPosition } from '../../types';
import { usePortfolioLens } from '../../contexts/PortfolioLensContext';

// Empty-state copy keyed to the active CC/CSP lens, so a CSP lens with no puts
// doesn't tell the user to "open a covered call".
const EMPTY_COPY: Record<'all' | 'cc' | 'csp', string> = {
  all: 'No active positions. Open a position to see portfolio composition analytics.',
  cc: 'No active covered calls. Open a covered call position to see composition analytics.',
  csp: 'No active cash-secured puts. Open a cash-secured put to see composition analytics.',
};

interface Props {
  data: CompositionData | null;
  loading: boolean;
}

const isPut = (p: CompositionPosition) => p.position_type === 'cash_secured_put';

/** Share-equivalent delta of the whole position: CC = shares − call delta, CSP = put delta. */
export const netDeltaShares = (p: CompositionPosition): number | null => {
  if (p.current_delta == null) return null;
  const d = Math.abs(p.current_delta);
  return Math.round((isPut(p) ? d : 1 - d) * 100 * p.num_contracts);
};

/** Theta as income for a short option: the API's theta is the option's (negative = decay we earn). */
export const thetaIncome = (theta: number | null | undefined): number | null => (theta == null ? null : -theta);

const money = (v: number | null | undefined, signed = false) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const r = Math.round(v);
  return `${signed && r > 0 ? '+' : r < 0 ? '−' : ''}${Math.abs(r).toLocaleString('en-US')}`;
};
const pct = (v: number | null | undefined, d = 1) => (v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(d)}%`);

type SortKey = 'weight' | 'ticker' | 'delta' | 'theta' | 'iv' | 'ivr' | 'dte' | 'ivhv';

/**
 * Portfolio composition (Oct 2026, trader theme): one exposure table instead
 * of donuts and KPI cards, then sectors, delta/DTE ladders and the 90-day
 * correlation matrix. API values only; "—" when missing.
 */
const PortfolioComposition: React.FC<Props> = ({ data, loading }) => {
  const { lens } = usePortfolioLens();
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'weight', dir: -1 });

  const rows = useMemo(() => {
    if (!data?.positions.length) return [];
    const v = (p: CompositionPosition): number => {
      switch (sort.key) {
        case 'ticker': return 0;
        case 'weight': return p.weight_pct ?? -Infinity;
        case 'delta': return netDeltaShares(p) ?? -Infinity;
        case 'theta': return thetaIncome(p.theta_daily) ?? -Infinity;
        case 'iv': return p.contract_iv ?? -Infinity;
        case 'ivr': return p.iv_rank ?? -Infinity;
        case 'dte': return p.days_to_expiration ?? -Infinity;
        case 'ivhv': return p.symbol_iv != null && p.symbol_hv_20 != null ? p.symbol_iv - p.symbol_hv_20 : -Infinity;
      }
    };
    return [...data.positions].sort((a, b) =>
      sort.key === 'ticker' ? a.ticker.localeCompare(b.ticker) * sort.dir : (v(a) - v(b)) * sort.dir || (b.weight_pct ?? -1) - (a.weight_pct ?? -1)
    );
  }, [data, sort]);

  const totals = useMemo(() => {
    if (!data?.positions.length) return null;
    const ps = data.positions;
    const deltas = ps.map(netDeltaShares).filter((x): x is number => x != null);
    return {
      notional: ps.reduce((s, p) => s + (p.notional ?? 0), 0),  // a position without a Price adds nothing
      contracts: ps.reduce((s, p) => s + p.num_contracts, 0),
      netDelta: deltas.length ? deltas.reduce((s, x) => s + x, 0) : null,
    };
  }, [data]);

  const deltaBuckets = useMemo(() => bucket(data?.positions ?? [], (p) => Math.abs(p.current_delta ?? p.entry_delta ?? NaN), [
    ['< .15', 0, 0.15], ['.15–.25', 0.15, 0.25], ['.25–.35', 0.25, 0.35], ['.35–.50', 0.35, 0.5], ['≥ .50', 0.5, Infinity],
  ]), [data]);
  const dteBuckets = useMemo(() => bucket(data?.positions ?? [], (p) => p.days_to_expiration ?? NaN, [
    ['0–7', 0, 8], ['8–21', 8, 22], ['22–45', 22, 46], ['46–90', 46, 91], ['> 90', 91, Infinity],
  ]), [data]);

  if (loading) return <p className="pc-empty">Loading portfolio composition…</p>;
  if (!data || !data.positions.length) return <p className="pc-empty">{EMPTY_COPY[lens]}</p>;

  const gs = data.greeks_summary;
  const head = (key: SortKey, label: string, title?: string, left = false) => (
    <th className={left ? 'pc-l' : undefined} title={title} aria-sort={sort.key === key ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
      <button
        type="button"
        className={`pc-sort ${sort.key === key ? 'is-on' : ''}`}
        onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : key === 'ticker' ? 1 : -1 }))}
      >
        {label}{sort.key === key ? (sort.dir === 1 ? ' ↑' : ' ↓') : ''}
      </button>
    </th>
  );
  const maxSector = Math.max(...data.sector_exposure.map((s) => s.weight_pct), 1);

  return (
    <div className="pc">
      {/* Summary strip */}
      <dl className="pc-strip">
        <div><dt>Positions</dt><dd>{data.positions.length}<small> · {totals?.contracts} ct</small></dd></div>
        <div><dt>Capital</dt><dd>{money(totals?.notional)}</dd></div>
        <div title="Share-equivalent delta of all positions (shares minus short calls; short puts at their delta)"><dt>Net Δ</dt><dd>{totals?.netDelta ?? '—'}<small> sh</small></dd></div>
        <div title="Sum of the short options' delta × 100 × contracts"><dt>Option Δ</dt><dd>{gs.total_delta_exposure.toFixed(0)}<small> sh</small></dd></div>
        <div title="Time decay earned per day by the short options"><dt>Θ / day</dt><dd className="pc-gain">{money(thetaIncome(gs.total_theta_daily), true)}</dd></div>
        <div><dt>Avg IV</dt><dd>{gs.weighted_avg_iv != null ? pct(gs.weighted_avg_iv * 100) : '—'}</dd></div>
        <div title="Weighted by capital"><dt>IV rank</dt><dd>{gs.weighted_avg_iv_rank != null ? Math.round(gs.weighted_avg_iv_rank) : '—'}</dd></div>
      </dl>

      {/* Exposure */}
      <section className="pc-sec" aria-label="Exposure by position">
        <div className="pc-h"><span>Exposure</span><small>Weight = share of capital · Θ positive = income · IV / HV = stock implied vs 20-day realized</small></div>
        <div className="pc-wrap">
          <table className="pc-tbl">
            <thead>
              <tr>
                {head('ticker', 'Position', undefined, true)}
                <th className="pc-l">Sector</th>
                {head('weight', 'Weight')}
                <th>Capital</th>
                <th>Ct</th>
                {head('delta', 'Net Δ', 'Shares minus call delta; puts at their delta')}
                <th title="Contract delta now (at entry)">Δ (entry)</th>
                {head('theta', 'Θ / day')}
                {head('iv', 'IV')}
                {head('ivr', 'IVR')}
                {head('dte', 'DTE')}
                {head('ivhv', 'IV / HV', 'Stock implied vol vs 20-day realized')}
              </tr>
            </thead>
            <tbody>
              {rows.map((p, i) => {
                const th = thetaIncome(p.theta_daily);
                const nd = netDeltaShares(p);
                const ivhv = p.symbol_iv != null && p.symbol_hv_20 != null ? p.symbol_iv - p.symbol_hv_20 : null;
                return (
                  <tr key={`${p.ticker}-${p.position_type}-${i}`}>
                    <td className="pc-l pc-tk">{p.ticker}<span className="pc-tag">{isPut(p) ? 'CSP' : 'CC'}</span></td>
                    <td className="pc-l pc-sector" title={p.sector}>{p.sector}</td>
                    <td className="pc-w"><span className="n">{pct(p.weight_pct)}</span><i style={{ width: `${Math.min(100, p.weight_pct ?? 0)}%` }} /></td>
                    <td className="n">{money(p.notional)}</td>
                    <td className="n">{p.num_contracts}</td>
                    <td className="n">{nd ?? '—'}</td>
                    <td className="n pc-dim">{p.current_delta != null ? Math.abs(p.current_delta).toFixed(2) : '—'}{p.entry_delta != null ? ` (${Math.abs(p.entry_delta).toFixed(2)})` : ''}</td>
                    <td className={`n ${th != null && th > 0 ? 'pc-gain' : th != null && th < 0 ? 'pc-loss' : ''}`}>{money(th, true)}</td>
                    <td className="n">{p.contract_iv != null ? pct(p.contract_iv * 100, 0) : '—'}</td>
                    <td className="n">{p.iv_rank != null ? Math.round(p.iv_rank) : '—'}</td>
                    <td className="n">{p.days_to_expiration ?? '—'}</td>
                    <td className="n">{p.symbol_iv != null && p.symbol_hv_20 != null
                      ? <>{Math.round(p.symbol_iv)} / {Math.round(p.symbol_hv_20)} <span className="pc-dim">{ivhv! >= 0 ? '+' : '−'}{Math.abs(Math.round(ivhv!))}</span></>
                      : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td className="pc-l">Total</td>
                <td />
                <td className="n">100%</td>
                <td className="n">{money(totals?.notional)}</td>
                <td className="n">{totals?.contracts}</td>
                <td className="n">{totals?.netDelta ?? '—'}</td>
                <td />
                <td className="n pc-gain">{money(thetaIncome(gs.total_theta_daily), true)}</td>
                <td className="n">{gs.weighted_avg_iv != null ? pct(gs.weighted_avg_iv * 100, 0) : '—'}</td>
                <td className="n">{gs.weighted_avg_iv_rank != null ? Math.round(gs.weighted_avg_iv_rank) : '—'}</td>
                <td /><td />
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      <div className="pc-row3">
        {/* Sectors */}
        <section className="pc-sec" aria-label="Sector exposure">
          <div className="pc-h"><span>Sectors</span><small>by capital</small></div>
          <table className="pc-tbl pc-mini">
            <tbody>
              {data.sector_exposure.map((s) => (
                <tr key={s.sector}>
                  <td className="pc-l pc-sector" title={s.sector}>{s.sector}</td>
                  <td className="pc-bar"><i style={{ width: `${(s.weight_pct / maxSector) * 100}%` }} /></td>
                  <td className="n">{pct(s.weight_pct)}</td>
                  <td className="n pc-dim">{s.position_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
        <Ladder title="Delta" note="contract Δ, positions" buckets={deltaBuckets} />
        <Ladder title="Days to expiry" note="positions" buckets={dteBuckets} />
      </div>

      {data.correlation_matrix.tickers.length >= 2 && data.correlation_matrix.matrix.length > 0 && (
        <section className="pc-sec" aria-label="Correlation">
          <div className="pc-h"><span>Correlation</span><small>90-day daily returns · brighter = moves together</small></div>
          <CorrelationMatrix tickers={data.correlation_matrix.tickers} matrix={data.correlation_matrix.matrix} />
        </section>
      )}
    </div>
  );
};

type Bucket = { label: string; count: number };
function bucket(ps: CompositionPosition[], val: (p: CompositionPosition) => number, defs: Array<[string, number, number]>): Bucket[] {
  return defs.map(([label, lo, hi]) => ({ label, count: ps.filter((p) => { const v = val(p); return Number.isFinite(v) && v >= lo && v < hi; }).length }));
}

const Ladder: React.FC<{ title: string; note: string; buckets: Bucket[] }> = ({ title, note, buckets }) => {
  const max = Math.max(...buckets.map((b) => b.count), 1);
  return (
    <section className="pc-sec" aria-label={title}>
      <div className="pc-h"><span>{title}</span><small>{note}</small></div>
      <table className="pc-tbl pc-mini">
        <tbody>
          {buckets.map((b) => (
            <tr key={b.label}>
              <td className="pc-l n">{b.label}</td>
              <td className="pc-bar"><i style={{ width: `${(b.count / max) * 100}%` }} /></td>
              <td className={`n ${b.count === 0 ? 'pc-dim' : ''}`}>{b.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

/** Correlation matrix: mono values, cell brightness = strength of positive co-movement. */
const CorrelationMatrix: React.FC<{ tickers: string[]; matrix: (number | null)[][] }> = ({ tickers, matrix }) => (
  <div className="pc-wrap">
    <table className="pc-corr">
      <thead>
        <tr><th />{tickers.map((t) => <th key={t}>{t}</th>)}</tr>
      </thead>
      <tbody>
        {tickers.map((row, i) => (
          <tr key={row}>
            <th className="pc-l">{row}</th>
            {(matrix[i] ?? []).map((v, j) => {
              if (i === j) return <td key={j} className="is-self">—</td>;
              const a = v == null ? 0 : Math.max(0, v);
              return (
                <td
                  key={j}
                  title={v != null ? `${row} / ${tickers[j]}: ${v.toFixed(3)}` : 'N/A'}
                  style={{ background: `rgba(231, 232, 234, ${(a * 0.32).toFixed(3)})` }}
                  className={v != null && v < 0 ? 'is-neg' : v != null && v >= 0.6 ? 'is-hi' : ''}
                >
                  {v == null ? '—' : v.toFixed(2)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default PortfolioComposition;
